import * as React from 'react';
import { connect } from 'react-redux';

import StoryContextMenu from '../story-context-menu/StoryContextMenu';
import StoryAttachmentUploader from '../story-attachment-uploader/StoryAttachmentUploader';
import StoryAttachment from '../story-attachment/StoryAttachment';
import { withModal } from 'components/modal/ModalHOC';

import { prepareDeliverableAttachmentsForGallery } from 'utils/deliverables';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import { __t, applyTranslations } from 'utils/language';

import './story-snippet-card.scss';

applyTranslations(require('./story-snippet-card.i18n.json'));

import {
  State
} from 'types';
import { Attachment } from '../../types/general';
import { StorySnippetValidationError } from '../../types/validation';

interface OwnProps {
  index: number,
  deliverableId: number,
  snippetId: number,
  story: null | Attachment,
  insights: Array<Attachment>,
  errors: Array<StorySnippetValidationError>
}

interface StateProps {
  canEdit: boolean
}

type Props = OwnProps & StateProps

const ModalGallery = withModal(<div />, WITH_GALLERY_MODAL_CONFIG);

export const StorySnippetCard = ({
  deliverableId,
  index,
  snippetId,
  canEdit,
  story,
  insights,
  errors
}: Props) => {
  const noStoryError = errors.find(er => er.type === 'no-story') ?? null;

  return (
    <div className="story-snippet-card">
      <div className="story-snippet-card__header">
        <span className="story-snippet-card__header-text">
          {__t('story_snippet_card.header', { index })}
        </span>
        <span className="story-snippet-card__header-menu">
          <StoryContextMenu
            snippetId={snippetId}
            deliverableId={deliverableId}
          />
        </span>
      </div>
      <div className="story-snippet-card__content">
        <div className="story-snippet-card__highres-content">
          <div className="story-snippet-card__subheader">
            {__t('story_snippet_card.screenshot_video')}
          </div>
          <div className="story-snippet-card__attachments">
            <div className="story-snippet-card__attachment-wrapper">
              {story ? (
                <div className="story-snippet-card__attachment">
                  <ModalGallery
                    modalContentProps={{
                      items: prepareDeliverableAttachmentsForGallery([
                        story,
                        ...insights
                      ]),
                      currentPosition: 0
                    }}
                  >
                    <StoryAttachment
                      key={story.id}
                      snippetId={snippetId}
                      deliverableId={deliverableId}
                      attachment={story}
                      type="highres"
                    />
                  </ModalGallery>
                </div>
              ) : null}
              {canEdit && !story ? (
                <StoryAttachmentUploader
                  type="highres"
                  snippetId={snippetId}
                  deliverableId={deliverableId}
                />
              ) : null}
              {!canEdit && !story ? (
                <div className="story-snippet-card__empty-content">
                  {__t('story_snippet_card.no_content')}
                </div>
              ) : null}
              {noStoryError ? (
                <div className="story-snippet-card__error">
                  {noStoryError.error}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="story-snippet-card__analytics-content">
          <div className="story-snippet-card__subheader">
            {__t('story_snippet_card.insights')}
          </div>
          <div className="story-snippet-card__attachments">
            {canEdit ? (
              <StoryAttachmentUploader
                type="analytics"
                snippetId={snippetId}
                deliverableId={deliverableId}
              />
            ) : null}
            {!canEdit && insights.length === 0 ? (
              <div className="story-snippet-card__empty-content">
                {__t('story_snippet_card.no_content')}
              </div>
            ) : null}
            {insights.map((aa, indx) => (
              <div
                className="story-snippet-card__attachment"
                key={aa.id}
              >
                <ModalGallery
                  modalContentProps={{
                    items: prepareDeliverableAttachmentsForGallery([
                      ...(story ? [story] : []),
                      ...insights
                    ]),
                    currentPosition: indx + 1
                  }}
                >
                  <StoryAttachment
                    snippetId={snippetId}
                    deliverableId={deliverableId}
                    attachment={aa}
                    type="analytics"
                  />
                </ModalGallery>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (
  { storySnippets }: State,
  { deliverableId }: OwnProps
): StateProps => ({
  canEdit: !!storySnippets.deliverables[deliverableId]?.canEdit
});

export default connect(mapStateToProps)(StorySnippetCard);
